import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import useSyncMutation from './useSyncMutation';

import { QUERY_KEY } from '~common';
import { useLanguageContext } from '~providers';
import { postRequest } from '~services';
import {
  UpdateUserPassStatusRequest,
  UserPassSummary,
} from '~types/subscription.type';
import {
  getSubscriptionErrorCode,
  getSubscriptionErrorMessage,
  SubscriptionErrorCode,
} from '~utils/subscription-error.util';

/**
 * Cancel or un-cancel a pass. Neither action moves money.
 *
 * `RENEW` is un-cancel, not "resubscribe" — restarting an *ended* pass is a new
 * purchase through the normal request-battery flow, so this endpoint answers
 * `USER_PASS_INVALID_STATE` for one.
 */
export const useMutationUpdateUserPassStatus = ({
  onSuccess,
}: {
  onSuccess?: (pass: UserPassSummary) => void;
} = {}) => {
  const { lt } = useLanguageContext();
  const queryClient = useQueryClient();

  return useSyncMutation<
    UserPassSummary,
    AxiosError<{ message?: string; stringCode?: string }>,
    UpdateUserPassStatusRequest
  >(
    (request) =>
      postRequest({
        path: 'api/user-pass/update-status',
        data: request,
      }).then((res) => res.data as UserPassSummary),
    {
      onSuccess: (pass) => {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY.GET_CURRENT_SUBSCRIPTION],
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY.GET_USER_PASS_DETAIL],
        });
        onSuccess?.(pass);
      },
      onError: (error) => {
        // The pass moved under us, so what this screen is showing is stale.
        // Refetching is what lets the user retry against reality.
        if (
          getSubscriptionErrorCode(error) ===
          SubscriptionErrorCode.USER_PASS_INVALID_STATE
        ) {
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY.GET_CURRENT_SUBSCRIPTION],
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY.GET_USER_PASS_DETAIL],
          });
        }

        // `USER_PASS_INVALID_STATE` is the common case here — say so in the
        // user's language rather than passing the backend's English through.
        toast.error(
          getSubscriptionErrorMessage(error, lt) ??
            error.response?.data?.message ??
            lt(
              'text.hooks.useMutationUpdateUserPassStatus.error',
              'Something went wrong when updating your pass',
            ),
        );
      },
    },
  );
};
